import { MEMBER_STATUS, PENDING_MEMBER_STATUSES, USER_TYPE } from '../constants/copy.js';
import { SHOW_DEPARTMENT_FIELD } from '../constants/memberDetail.js';

const SIGNED_IN_FIELDS = ['email', 'joined', 'lastActive'];

/** Per status: badge, banner and the card fields (before the optional department). */
const STATUS_VIEW = Object.freeze({
  [MEMBER_STATUS.ACTIVE]: { banner: null, fields: SIGNED_IN_FIELDS },
  [MEMBER_STATUS.SUSPENDED]: { banner: null, fields: SIGNED_IN_FIELDS },
  [MEMBER_STATUS.REMOVED]: { banner: null, fields: SIGNED_IN_FIELDS },
  [MEMBER_STATUS.INVITED]: { banner: 'invited', fields: ['email', 'invitationSent'] },
  [MEMBER_STATUS.EXPIRED]: { banner: 'expired', fields: ['email', 'invitationSent'] },
  [MEMBER_STATUS.NOT_INVITED]: { banner: 'notInvited', fields: ['email', 'added', 'invitation'] },
});

/**
 * What the info card shows for a status: `{ badgeStatus, banner, fields }`. `fields` are keys
 * rendered by `MemberInfoCard`; USER TYPE is always last, DEPARTMENT only when enabled.
 *
 * @param {string} uiStatus a `MEMBER_STATUS` value
 * @param {{ showDepartment?: boolean }} [options]
 */
export const getMemberStatusView = (uiStatus, { showDepartment = SHOW_DEPARTMENT_FIELD } = {}) => {
  const view = STATUS_VIEW[uiStatus] ?? STATUS_VIEW[MEMBER_STATUS.ACTIVE];
  return {
    badgeStatus: uiStatus,
    banner: view.banner,
    fields: [...view.fields, ...(showDepartment ? ['department'] : []), 'userType'],
  };
};

/** Pending members whose invitation was sent, so it can be revoked. */
const REVOCABLE_STATUSES = [MEMBER_STATUS.INVITED, MEMBER_STATUS.EXPIRED];
const REMOVE_LABEL_KEY = 'memberDetail.quickActions.remove';
const DELETE_CONTACT_LABEL_KEY = 'memberDetail.quickActions.deleteContact';

const hidden = { visible: false, disabledReason: null };

/**
 * Which actions the actor has on a member (the status/type rules in ONE place). Permission keys
 * are checked by the components (`Can`); this only answers "does the action apply, and if not
 * allowed, why". `disabledReason` is an i18n key shown in a tooltip.
 *
 * @param {object} args
 * @param {string} args.uiStatus a `MEMBER_STATUS` value
 * @param {string} args.userType user type of the member
 * @param {string} args.actorUserType user type of the signed-in actor
 * @param {boolean} [args.actorCanRemoveAdmin] actor holds the dedicated "remove an Admin" permission
 */
export const getMemberActions = ({ uiStatus, userType, actorUserType, actorCanRemoveAdmin = false }) => {
  const isOwner = userType === USER_TYPE.OWNER;

  // Remove and Delete user share one protection: never the Owner; an Admin only by the Owner
  // or by an Admin holding the dedicated permission.
  let removeReason = null;
  if (isOwner) removeReason = 'memberDetail.owner.removeDisabled';
  else if (userType === USER_TYPE.ADMIN && actorUserType !== USER_TYPE.OWNER && !actorCanRemoveAdmin) {
    removeReason = 'memberDetail.admin.removeDisabled';
  }

  if (uiStatus === MEMBER_STATUS.REMOVED) {
    return {
      resetPassword: false,
      suspend: hidden,
      remove: hidden,
      reactivate: true,
      reactivateLabelKey: 'memberDetail.quickActions.reactivate',
      revoke: false,
      removeLabelKey: REMOVE_LABEL_KEY,
      deleteUser: { visible: true, disabledReason: removeReason },
      userTypeAction: null,
    };
  }

  const isActive = uiStatus === MEMBER_STATUS.ACTIVE;

  let userTypeAction = null;
  if (userType === USER_TYPE.MEMBER) userTypeAction = 'makeAdmin';
  else if (userType === USER_TYPE.ADMIN) userTypeAction = 'makeMember';

  return {
    // A suspended member can still get a reset link (as in the prototype): it works once reactivated.
    resetPassword: isActive || uiStatus === MEMBER_STATUS.SUSPENDED,
    suspend: isActive
      ? { visible: true, disabledReason: isOwner ? 'memberDetail.owner.suspendDisabled' : null }
      : hidden,
    remove: { visible: true, disabledReason: removeReason },
    // A suspended member can be reactivated from the detail too (CT-5827), like the row menu.
    reactivate: uiStatus === MEMBER_STATUS.SUSPENDED,
    reactivateLabelKey: 'memberDetail.quickActions.reactivateAccess',
    // A pending invitation (sent or expired) can be revoked; not-invited ones were never sent.
    revoke: REVOCABLE_STATUSES.includes(uiStatus),
    // Somebody who never signed in is a contact, not a member of the workspace yet.
    removeLabelKey: PENDING_MEMBER_STATUSES.includes(uiStatus) ? DELETE_CONTACT_LABEL_KEY : REMOVE_LABEL_KEY,
    deleteUser: hidden,
    userTypeAction,
  };
};
